import {Tag, Tooltip, Button, Typography} from 'antd';
import dayjs from 'dayjs';
import NumberFormat from 'react-number-format';
import React from 'react';
import {
  ExportOutlined,
  CalendarOutlined,
  UserOutlined,
  DollarOutlined,
  PercentageOutlined,
  BankOutlined,
} from '@ant-design/icons';

const {Text} = Typography;

// Estilos para las columnas
const columnStyles = {
  codeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  codeText: {
    fontWeight: 600,
    color: '#262626',
    fontSize: '13px',
  },
  linkButton: {
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    border: 'none',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  dateContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  clientContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  montoContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  montoValue: {
    fontWeight: 600,
    fontSize: '13px',
  },
  saldoContainer: {
    background: '#f6ffed',
    border: '1px solid #b7eb8f',
    borderRadius: '6px',
    padding: '4px 8px',
    display: 'inline-block',
  },
  saldoCapitalContainer: {
    background: '#e6f7ff',
    border: '1px solid #91d5ff',
    borderRadius: '6px',
    padding: '4px 8px',
    display: 'inline-block',
  },
};

const columns = () => {
  const cols = [
    {
      title: (
        <Text strong style={{fontSize: '12px'}}>
          Código
        </Text>
      ),
      key: 'codigo',
      dataIndex: 'codigo_contrato',
      width: 140,
      render: (_, {codigo_contrato}) => (
        <div style={columnStyles.codeContainer}>
          <Text style={columnStyles.codeText}>{codigo_contrato}</Text>
          <Tooltip title='Abrir contrato en nueva pestaña'>
            <Button
              type='primary'
              size='small'
              style={columnStyles.linkButton}
              icon={<ExportOutlined style={{color: 'white', fontSize: '12px'}} />}
              onClick={() =>
                window.open('/contrato/' + codigo_contrato, '_blank')
              }
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: (
        <Text strong style={{fontSize: '12px'}}>
          Tipo
        </Text>
      ),
      key: 'tipo',
      width: 120,
      render: (_, {tipo_contrato}) => (
        <Tag
          color={tipo_contrato.tipo_contrato_color}
          style={{
            borderRadius: '6px',
            fontWeight: 500,
            fontSize: '11px',
            padding: '2px 8px',
          }}>
          {tipo_contrato.nombre_contrato}
        </Tag>
      ),
    },
    {
      title: (
        <Text strong style={{fontSize: '12px'}}>
          Estado
        </Text>
      ),
      key: 'estado',
      width: 180,
      render: (_, contrato) => (
        <Tag
          color={
            contrato.estado_contrato.rgb_estado_contrato ||
            contrato.estado_contrato.color_estado_contrato
          }
          style={{
            borderRadius: '8px',
            fontWeight: 500,
            fontSize: '11px',
            padding: '4px 10px',
          }}>
          {contrato.estado_contrato.nombre_estado_contrato}
        </Tag>
      ),
    },
    {
      title: (
        <Text strong style={{fontSize: '12px'}}>
          <CalendarOutlined style={{marginRight: '4px'}} />
          Fecha de pago
        </Text>
      ),
      key: 'fechaCreacion',
      width: 130,
      render: (_, {fecha_fin_contrato}) => (
        <div style={columnStyles.dateContainer}>
          <CalendarOutlined style={{color: '#8c8c8c', fontSize: '12px'}} />
          <Text style={{fontSize: '13px'}}>
            {dayjs(fecha_fin_contrato).add(6, 'hours').format('DD/MM/YYYY')}
          </Text>
        </div>
      ),
    },
    {
      title: (
        <Text strong style={{fontSize: '12px'}}>
          <UserOutlined style={{marginRight: '4px'}} />
          Cliente
        </Text>
      ),
      key: 'cliente',
      width: 200,
      render: (_, {actores_contrato}) => (
        <div style={columnStyles.clientContainer}>
          <UserOutlined style={{color: '#667eea', fontSize: '12px'}} />
          <Text ellipsis style={{maxWidth: '170px', fontSize: '13px'}}>
            {actores_contrato[0].cliente.nombre_cliente}{' '}
            {actores_contrato[0].cliente.apellido_cliente}
          </Text>
        </div>
      ),
    },
    {
      title: (
        <Text strong style={{fontSize: '12px'}}>
          <BankOutlined style={{marginRight: '4px'}} />
          Monto otorgado
        </Text>
      ),
      key: 'montoOtorgado',
      width: 140,
      render: (_, {monto_total_prestamo}) => (
        <Text strong style={{...columnStyles.montoValue, color: '#52c41a'}}>
          <NumberFormat
            value={monto_total_prestamo}
            displayType={'text'}
            thousandSeparator={true}
            prefix={'Q '}
          />
        </Text>
      ),
    },
    {
      title: (
        <Text strong style={{fontSize: '12px'}}>
          <PercentageOutlined style={{marginRight: '4px'}} />
          Saldo interés
        </Text>
      ),
      key: 'saldointeres',
      width: 130,
      render: (_, {saldo_actual}) => (
        <div style={columnStyles.saldoContainer}>
          <Text style={{fontSize: '12px', color: '#389e0d'}}>
            <NumberFormat
              value={parseFloat(saldo_actual[0].saldo_intereses).toFixed(2)}
              displayType={'text'}
              thousandSeparator={true}
              prefix={'Q '}
            />
          </Text>
        </div>
      ),
    },
    {
      title: (
        <Text strong style={{fontSize: '12px'}}>
          <DollarOutlined style={{marginRight: '4px'}} />
          Saldo capital
        </Text>
      ),
      key: 'saldocapital',
      width: 130,
      render: (_, {saldo_actual}) => (
        <div style={columnStyles.saldoCapitalContainer}>
          <Text style={{fontSize: '12px', color: '#0958d9'}}>
            <NumberFormat
              value={parseFloat(saldo_actual[0].saldo_capital).toFixed(2)}
              displayType={'text'}
              thousandSeparator={true}
              prefix={'Q '}
            />
          </Text>
        </div>
      ),
    },
  ];
  return cols;
};

export default columns;
